import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanDeactivate, RouterStateSnapshot, UrlTree } from '@angular/router';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Observable, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { TimesheetCanDeactivateHost } from './timesheet-can-deactivate.interface';

@Injectable({
    providedIn: 'root',
})
export class TimesheetCanDeactivateGuard implements CanDeactivate<TimesheetCanDeactivateHost> {
    public constructor(@Inject(LogInstanceToken) private readonly log: LogInterface) {}

    /**
     * Decides if the timesheet route can be left, which is true after the timesheet content was auto-saved.
     * The current implementation needs to support both the legacy TimesheetAndExpensesComponent and the new ngrx based
     * TimesheetAndExpensesNgrxComponent; therefore we delegate saving to the component to implement it without the
     * the guard needing to know how data is handled.
     *
     * A better approach would be to directly throw a timesheet save action and listen for the success action, but we
     * can only implement that once the legacy path has been removed.
     *
     * Therefore:
     * TODO: refactor
     *       - directly throw a timesheet save action
     *       - migrate to [canDeactivateFn](https://angular.dev/api/router/CanDeactivateFn) approach
     *
     * @param component currently active component, ie. TimesheetAndExpensesComponent of some sort
     * @param currentRoute current route
     * @param currentState courrent router state
     * @param nextState next router state
     *
     * @returns Observable of bool that indicates if the route can be left
     */
    public canDeactivate(
        component: TimesheetCanDeactivateHost,
        /* eslint-disable @typescript-eslint/no-unused-vars */
        currentRoute: ActivatedRouteSnapshot,
        currentState: RouterStateSnapshot,
        nextState?: RouterStateSnapshot,
    ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        this.log.debug(
            `TimesheetCanDeactivateGuard: canDeactivate: Triggering auto-save of timesheet when leaving the timesheet. (${Date.now()})`,
        );
        component
            .saveBeforeDeactivate()
            .pipe(
                take(1),
                catchError((err) => {
                    this.log.error(`Saving timesheet failed: ${JSON.stringify(err)}`);
                    return of(undefined);
                }),
            )
            .subscribe();

        return true;
    }
}
